import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { settingsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FaHeart, FaUser, FaStar, FaCheck } from 'react-icons/fa';

const Wizard = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    sexual_preference: '3',
    sexual_orientation: '1',
    age_min: 18,
    age_max: 100,
    interests: '',
  });

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    try {
      setLoading(true);
      await settingsAPI.updatePreferences(preferences);
      toast.success('Profile setup complete! 🎉');
      navigate('/encounters');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIcon = (stepNum) => {
    if (stepNum < step) {
      return <FaCheck className="step-icon completed" />;
    } else if (stepNum === step) {
      return <div className="step-icon active">{stepNum}</div>;
    } else {
      return <div className="step-icon">{stepNum}</div>;
    }
  };

  return (
    <div className="wizard-container">
      <div className="wizard-wrapper">
        <div className="wizard-header">
          <h1 className="wizard-title">
            <FaHeart className="title-icon" />
            Complete Your Profile
          </h1>
          <p className="wizard-subtitle">Help us find your perfect match</p>
        </div>

        {/* Progress Bar */}
        <div className="wizard-progress">
          <div className="progress-bar-container">
            <div className="progress-bar" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="progress-steps">
            <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
              {renderStepIcon(1)}
              <span className="step-label">Preferences</span>
            </div>
            <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
              {renderStepIcon(2)}
              <span className="step-label">Details</span>
            </div>
            <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
              {renderStepIcon(3)}
              <span className="step-label">Interests</span>
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="wizard-content">
          <div className={`wizard-step ${step === 1 ? 'active' : ''}`}>
            {step === 1 && (
              <div className="step-container fade-in">
                <div className="step-header">
                  <FaUser className="step-header-icon" />
                  <h2>Who are you interested in?</h2>
                  <p>Choose who you'd like to meet</p>
                </div>

                <div className="preference-cards">
                  <div
                    className={`preference-card ${preferences.sexual_preference === '1' ? 'selected' : ''}`}
                    onClick={() => setPreferences({ ...preferences, sexual_preference: '1' })}
                  >
                    <div className="card-icon">👨</div>
                    <h3>Men</h3>
                    <p>I'm interested in men</p>
                    {preferences.sexual_preference === '1' && (
                      <div className="selected-badge">
                        <FaCheck />
                      </div>
                    )}
                  </div>

                  <div
                    className={`preference-card ${preferences.sexual_preference === '2' ? 'selected' : ''}`}
                    onClick={() => setPreferences({ ...preferences, sexual_preference: '2' })}
                  >
                    <div className="card-icon">👩</div>
                    <h3>Women</h3>
                    <p>I'm interested in women</p>
                    {preferences.sexual_preference === '2' && (
                      <div className="selected-badge">
                        <FaCheck />
                      </div>
                    )}
                  </div>

                  <div
                    className={`preference-card ${preferences.sexual_preference === '3' ? 'selected' : ''}`}
                    onClick={() => setPreferences({ ...preferences, sexual_preference: '3' })}
                  >
                    <div className="card-icon">👥</div>
                    <h3>Both</h3>
                    <p>I'm interested in everyone</p>
                    {preferences.sexual_preference === '3' && (
                      <div className="selected-badge">
                        <FaCheck />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={`wizard-step ${step === 2 ? 'active' : ''}`}>
            {step === 2 && (
              <div className="step-container fade-in">
                <div className="step-header">
                  <FaHeart className="step-header-icon" />
                  <h2>Tell us more about you</h2>
                  <p>Help us personalize your experience</p>
                </div>

                <div className="form-section">
                  <div className="form-group">
                    <label className="form-label">Sexual Orientation</label>
                    <select
                      className="form-control modern-select"
                      value={preferences.sexual_orientation}
                      onChange={(e) =>
                        setPreferences({ ...preferences, sexual_orientation: e.target.value })
                      }
                    >
                      <option value="1">Straight</option>
                      <option value="2">Gay</option>
                      <option value="3">Lesbian</option>
                      <option value="4">Bisexual</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Age Range Preference</label>
                    <div className="age-range-container">
                      <div className="age-input-group">
                        <label className="age-label">From</label>
                        <input
                          type="number"
                          className="form-control age-input"
                          value={preferences.age_min}
                          min="18"
                          max="100"
                          onChange={(e) => setPreferences({ ...preferences, age_min: e.target.value })}
                        />
                      </div>
                      <div className="age-separator">—</div>
                      <div className="age-input-group">
                        <label className="age-label">To</label>
                        <input
                          type="number"
                          className="form-control age-input"
                          value={preferences.age_max}
                          min="18"
                          max="100"
                          onChange={(e) => setPreferences({ ...preferences, age_max: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="age-display">
                      Looking for people aged {preferences.age_min} to {preferences.age_max}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={`wizard-step ${step === 3 ? 'active' : ''}`}>
            {step === 3 && (
              <div className="step-container fade-in">
                <div className="step-header">
                  <FaStar className="step-header-icon" />
                  <h2>What are your interests?</h2>
                  <p>Share what you love to do</p>
                </div>

                <div className="form-section">
                  <div className="form-group">
                    <label className="form-label">Your Interests</label>
                    <textarea
                      className="form-control modern-textarea"
                      rows="6"
                      placeholder="e.g., Music, Sports, Travel, Movies, Photography, Cooking..."
                      value={preferences.interests}
                      onChange={(e) => setPreferences({ ...preferences, interests: e.target.value })}
                    />
                    <div className="form-hint">
                      💡 Tip: Add interests that you're passionate about. This helps us find better matches!
                    </div>
                  </div>

                  <div className="interests-suggestions">
                    <p className="suggestions-label">Popular interests:</p>
                    <div className="interest-tags">
                      {['Music', 'Travel', 'Sports', 'Movies', 'Photography', 'Cooking', 'Reading', 'Gaming'].map(
                        (interest) => (
                          <button
                            key={interest}
                            className="interest-tag"
                            onClick={() => {
                              const current = preferences.interests;
                              const newInterests = current
                                ? `${current}, ${interest}`
                                : interest;
                              setPreferences({ ...preferences, interests: newInterests });
                            }}
                          >
                            + {interest}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="wizard-footer">
          {step > 1 && (
            <button className="btn btn-back" onClick={() => setStep(step - 1)} disabled={loading}>
              ← Back
            </button>
          )}
          <button
            className={`btn btn-next ${step === totalSteps ? 'btn-complete' : ''}`}
            onClick={handleNext}
            disabled={loading}
          >
            {step === totalSteps ? (
              loading ? (
                <>
                  <span className="spinner"></span> Completing...
                </>
              ) : (
                <>
                  Complete <FaCheck />
                </>
              )
            ) : (
              <>
                Next →
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Wizard;
